import { useEffect, useRef, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { useDebouncedCallback } from '@wf/components';

import { getRestrictionsByCodes } from '../utils/getRestrictionsByEntity';

import { type RimmEntityType } from '../data/rimmEntityEnums';
import { type TRimmEntity } from '../data/rimmEntityTypes';
import { rimmEntitySelector } from '../store/selectors';
import { setEntityData } from '../store/slices';
import { fetchRimmEntity } from '../services';

export const useRimmEntity = <T extends RimmEntityType>(
  entity: T,
  minSuggest?: number,
  outerRimmServerURL?: string
) => {
  const dispatch = useDispatch();
  const { data: rimmEntities } = useSelector(rimmEntitySelector(entity));

  const [loading, setLoading] = useState(false);

  const [localRimmEntities, setLocalRimmEntities] = useState<TRimmEntity[T][]>([]);

  const abortController = useRef<AbortController>();

  const minSuggestIsUndefined = typeof minSuggest === 'undefined';

  useEffect(() => {
    if (!minSuggestIsUndefined || rimmEntities.length) {
      return;
    }

    setLoading(true);
    fetchRimmEntity({ entity, outerRimmServerURL })
      .then(({ data }) => {
        dispatch(setEntityData({ data, entity }));
      })
      .catch((error) => console.error(error))
      .finally(() => {
        setLoading(false);
      });
  }, [dispatch, entity, minSuggestIsUndefined, outerRimmServerURL, rimmEntities.length]);

  const handleDebounceSearch = useDebouncedCallback(
    (args: { searchText: string; searchByCodesOnly: boolean }) => {
      abortController.current?.abort();

      const { searchText, searchByCodesOnly } = args;
      const restrictions = searchByCodesOnly ? getRestrictionsByCodes([searchText]) : undefined;

      if (searchText.length < (minSuggest as number)) {
        setLocalRimmEntities([]);

        return;
      }

      abortController.current = new AbortController();
      const signal = abortController.current?.signal;

      fetchRimmEntity({
        entity,
        searchText,
        outerRimmServerURL,
        signal,
        restrictions,
      })
        .then(({ data }) => {
          setLocalRimmEntities(data.resultList as TRimmEntity[T][]);
        })
        .catch((error) => {
          console.error(error);
        })
        .finally(() => {
          if (!signal?.aborted) {
            setLoading(false);
          }
        });
    },
    500,
    [entity, minSuggest, outerRimmServerURL]
  );

  const handleSearch = (searchText: string, searchByCodesOnly = false) => {
    if (minSuggestIsUndefined) {
      return;
    }

    setLoading(searchText.length >= minSuggest);

    handleDebounceSearch({ searchText, searchByCodesOnly });
  };

  return {
    rimmEntities: minSuggestIsUndefined ? (rimmEntities as TRimmEntity[T][]) : localRimmEntities,
    onSearch: handleSearch,
    loading,
  };
};
